'use client'

import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import { alpha } from '@mui/material/styles'
import { Box, Container, Grid, Typography } from '@mui/material'

// Types
import type { SolutionCtaProps } from '@/types/components/organisms/solution'

// Utils
import { localizeHref } from '@/utils/theme/nav-menu'

// Components
const RevealAtom = dynamic(() => import('@/components/atoms/animation/reveal'))
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))

// Closing banner with the demo button
const SolutionCtaOrganism = (props: SolutionCtaProps) => {
	// Props
	const { lng, locale, title, subtitle, image, action } = props

	// Variables
	const primary = action ?? { text: locale.links.requestDemo, href: '/signup' }

	return (
		<Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
			<RevealAtom>
				<Box
					sx={{
						position: 'relative',
						overflow: 'hidden',
						borderRadius: { xs: 6, md: 10 },
						background: t => `linear-gradient(120deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
						color: 'primary.contrastText'
					}}
				>
					<Grid container sx={{ alignItems: 'stretch' }}>
						<Grid size={{ xs: 12, md: 7 }} sx={{ p: { xs: 4, md: 8 }, position: 'relative', zIndex: 1 }}>
							<Typography component="h2" sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 700, lineHeight: 1.5, color: 'inherit' }}>
								{title}
							</Typography>

							<Typography sx={{ mt: 2, fontSize: { xs: 16, md: 17 }, lineHeight: 1.9, color: alpha('#fff', 0.85) }}>{subtitle}</Typography>

							<Box
								sx={{
									mt: 4,
									'& .MuiButton-root': {
										bgcolor: 'secondary.main',
										// Dark text keeps the gold button readable (white on gold fails contrast)
										color: 'text.primary',
										px: 4,
										fontWeight: 500,
										'&:hover': { bgcolor: 'secondary.light' }
									}
								}}
							>
								<Link href={localizeHref(lng, primary.href)}>
									<PrimaryButtonAtom text={primary.text} size="large" />
								</Link>
							</Box>
						</Grid>

						<Grid size={{ xs: 12, md: 5 }} sx={{ position: 'relative', minHeight: { xs: 260, md: 'auto' } }}>
							<Image src={image} alt="" fill sizes="(max-width: 900px) 100vw, 40vw" style={{ objectFit: 'cover' }} />

							{/* Blend the photo into the gradient */}
							<Box
								sx={{
									position: 'absolute',
									inset: 0,
									background: t => `linear-gradient(${t.direction === 'rtl' ? 'to left' : 'to right'}, ${t.palette.primary.main} 0%, transparent 35%)`
								}}
							/>
						</Grid>
					</Grid>
				</Box>
			</RevealAtom>
		</Container>
	)
}

export default SolutionCtaOrganism
